import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Loader2, MailCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { AuthLayout } from './AuthLayout';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
    } catch {
      // The endpoint never reveals whether an account exists, and neither do we.
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  }

  if (sent) {
    return (
      <AuthLayout title="Check your email" subtitle="If that address has an account, a reset link is on its way.">
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <MailCheck className="size-7 text-[var(--accent)]" />
          <p className="text-[13px] vt-muted">The link expires in 30 minutes and can be used once.</p>
          <Button asChild variant="secondary" size="md">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password" subtitle="We will email you a single-use link.">
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <Button type="submit" variant="primary" size="lg" disabled={submitting}>
          {submitting ? <Loader2 className="animate-spin" /> : null}
          Send reset link
        </Button>
        <Link to="/login" className="text-center text-[12px] text-[var(--accent)] hover:underline">
          Back to sign in
        </Link>
      </form>
    </AuthLayout>
  );
}
